import { type CSSProperties } from 'react';
import { type DayLayoutFunction } from 'react-big-calendar';
import type { BlockReadiness } from '@features/eligibility-checks/types/readiness';

import type { CalendarEvent } from './types';

/**
 * Density preprocessing + layout algorithms for overlapping appointments.
 *
 * The production schedule uses `bucketClusterize` + `sideBySideLanesLayout`
 * ("capacity clusters"): collision clusters past the pass-through threshold
 * collapse into synthetic hour-bucket cluster cards, rendered by
 * `ClusterEventCard`. The other strategies here (`capWithOverflow` collector
 * pills, `createBoundedCascadeLayout`) are the alternative variants from the
 * density design analysis and are exercised by the Calendar Density Variants
 * Storybook stories.
 *
 * Pattern provenance (full audit: plot artifacts/research/calendar-overlap-audit.md):
 * clusters generalize the DevExtreme appointment collector / month-view
 * "+N more" into the time grid; lanes are the Outlook/Google interval
 * partitioning; the cascade mirrors Google/Apple stagger with Bryntum's
 * geometry knobs.
 */

export type DensityEventKind = 'overflow' | 'cluster';

export interface DensityCalendarEvent extends CalendarEvent {
  /** Synthetic event marker. Undefined = a real appointment. */
  kind?: DensityEventKind;
  /** Real events represented by a synthetic overflow/cluster card. */
  members?: CalendarEvent[];
}

/** Alias used by the production calendar for clarity at call sites. */
export type ClusteredCalendarEvent = DensityCalendarEvent;

/** Action-state rollup shown in cluster card headers ("2 blocked · 3 collect"). */
export interface ClusterReadinessRollup {
  blocked: number;
  attention: number;
  collect: number;
  cancelled: number;
}

const ROLLUP_READINESS: ReadonlySet<BlockReadiness> = new Set(['blocked', 'attention', 'collect']);

export const summarizeClusterReadiness = (
  members: CalendarEvent[],
): ClusterReadinessRollup => {
  const rollup: ClusterReadinessRollup = { blocked: 0, attention: 0, collect: 0, cancelled: 0 };

  members.forEach(member => {
    const blockState = member.resource.block_state;
    if (blockState?.reality === 'cancelled') {
      rollup.cancelled += 1;
      return;
    }
    const readiness = blockState?.readiness ?? null;
    if (readiness && ROLLUP_READINESS.has(readiness)) {
      rollup[readiness as 'blocked' | 'attention' | 'collect'] += 1;
    }
  });

  return rollup;
};

type DensityEventStyle = CSSProperties & { xOffset: number };

interface StyledLayoutEvent {
  event: CalendarEvent;
  style: DensityEventStyle;
}

// react-big-calendar types `slotMetrics`/`accessors` as `any`; these are the
// shapes the library actually passes (mirrors what denseOverlapLayout relies
// on), typed locally so the layout code stays lint-clean.
interface SlotRange {
  top: number;
  height: number;
  start: number;
  end: number;
  startDate: Date;
  endDate: Date;
}

interface SlotMetricsLike {
  getRange: (start: Date, end: Date) => SlotRange;
}

interface EventAccessorsLike {
  start: (event: CalendarEvent) => Date;
  end: (event: CalendarEvent) => Date;
}

interface TimedEvent {
  event: DensityCalendarEvent;
  startMs: number;
  endMs: number;
}

const toTimedSorted = (events: DensityCalendarEvent[]): TimedEvent[] =>
  events
    .map(event => ({
      event,
      startMs: event.start.getTime(),
      endMs: event.end.getTime(),
    }))
    .sort((a, b) => a.startMs - b.startMs || b.endMs - a.endMs);

/**
 * Connected components of transitively-overlapping events ("collision
 * groups" in the Google day-view algorithm). Touching endpoints do not
 * overlap.
 */
const findTimedClusters = (timed: TimedEvent[]): TimedEvent[][] => {
  const clusters: TimedEvent[][] = [];
  let current: TimedEvent[] = [];
  let maxEnd = Number.NEGATIVE_INFINITY;

  timed.forEach(item => {
    if (current.length > 0 && item.startMs >= maxEnd) {
      clusters.push(current);
      current = [];
      maxEnd = Number.NEGATIVE_INFINITY;
    }
    current.push(item);
    maxEnd = Math.max(maxEnd, item.endMs);
  });

  if (current.length > 0) clusters.push(current);
  return clusters;
};

/** Greedy first-fit lane assignment within one cluster (sorted by start). */
const assignLanes = (
  cluster: TimedEvent[],
): { laneOf: Map<DensityCalendarEvent, number>; laneCount: number } => {
  const laneEnds: number[] = [];
  const laneOf = new Map<DensityCalendarEvent, number>();

  cluster.forEach(item => {
    let lane = laneEnds.findIndex(end => end <= item.startMs);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(item.endMs);
    } else {
      laneEnds[lane] = item.endMs;
    }
    laneOf.set(item.event, lane);
  });

  return { laneOf, laneCount: laneEnds.length };
};

const bucketKey = (date: Date, bucketMinutes: number): number => {
  const minutes = date.getHours() * 60 + date.getMinutes();
  return Math.floor(minutes / bucketMinutes);
};

/**
 * Variant 1 preprocessing — "max-stack + overflow collector".
 *
 * STORYBOOK ONLY. The production schedule pairs `sideBySideLanesLayout` with
 * `bucketClusterize` (capacity clusters), which never emits `kind: 'overflow'`
 * events — so the collector-pill branch in `sideBySideLanesLayout` is dead in
 * the app and exercised only by the "Variant 1" density story. Kept here so
 * the design-analysis variants stay runnable from one module.
 *
 * Events that would land in lane >= maxLanes are removed and replaced by
 * slim synthetic "+N" pills, one per hour bucket so the collector stays
 * local to where the hidden appointments actually are (FullCalendar renders
 * its `eventMaxStack` more-link per slot region the same way). Pill display
 * spans are clamped to their hour bucket so pills never stack on each other
 * and never add lanes — the layout pins them to a fixed right-edge collector
 * column, like DevExtreme's compact Day/Week collector.
 *
 * Greedy lane order = (start asc, duration desc), so the earliest-starting
 * appointments stay visible. Swapping in an urgency-aware comparator is the
 * lever for "keep flagged appointments visible".
 */
export const capWithOverflow = (
  events: DensityCalendarEvent[],
  maxLanes: number,
): DensityCalendarEvent[] => {
  const result: DensityCalendarEvent[] = [];
  const MIN_PILL_MINUTES = 20;

  findTimedClusters(toTimedSorted(events)).forEach(cluster => {
    const { laneOf } = assignLanes(cluster);
    const visible = cluster.filter(item => (laneOf.get(item.event) ?? 0) < maxLanes);
    const hidden = cluster.filter(item => (laneOf.get(item.event) ?? 0) >= maxLanes);

    result.push(...visible.map(item => item.event));
    if (hidden.length === 0) return;

    const buckets = new Map<number, TimedEvent[]>();
    hidden.forEach(item => {
      const key = bucketKey(item.event.start, 60);
      buckets.set(key, [...(buckets.get(key) ?? []), item]);
    });

    buckets.forEach((bucketed, key) => {
      const first = bucketed[0];
      const start = new Date(Math.min(...bucketed.map(item => item.startMs)));
      const bucketEnd = new Date(start);
      // Clamp the final-hour bucket so setHours doesn't roll to the next day.
      bucketEnd.setHours(Math.min(key + 1, 23), key + 1 >= 24 ? 59 : 0, 0, 0);
      const minEnd = new Date(start.getTime() + MIN_PILL_MINUTES * 60_000);
      const end = new Date(
        Math.max(
          minEnd.getTime(),
          Math.min(bucketEnd.getTime(), Math.max(...bucketed.map(item => item.endMs))),
        ),
      );

      result.push({
        id: `overflow-${first.event.id}-${key}`,
        title: `+${bucketed.length} more`,
        start,
        end,
        resourceId: first.event.resourceId,
        resource: first.event.resource,
        kind: 'overflow',
        members: bucketed.map(item => item.event),
      });
    });
  });

  return result.sort((a, b) => a.start.getTime() - b.start.getTime());
};

/**
 * Variant 3 preprocessing — "capacity clusters" (the production path).
 *
 * Collision clusters bigger than `passThroughMax` are sliced into hour
 * buckets (by start time); each bucket with 3+ appointments becomes one
 * synthetic cluster card. Small clusters and small buckets pass through as
 * regular cards, so a quiet solo-provider day renders identically to today.
 *
 * Bucketing is by START time: an appointment that begins at 9:50 and runs to
 * 11:00 is assigned to the 9:xx bucket only. If the 10:xx hour has fewer than
 * `passThroughMax` other events, those render as individual cards while the
 * 9:50 appointment stays inside the 9:xx cluster — so a long appointment has
 * no card in the later hour it spills into. The cluster card's mini-agenda
 * shows the real end time, so opening it recovers the full picture; this is an
 * accepted tradeoff of start-time bucketing, not a bug.
 */
export const bucketClusterize = (
  events: DensityCalendarEvent[],
  { passThroughMax = 2, bucketMinutes = 60 }: { passThroughMax?: number; bucketMinutes?: number } = {},
): DensityCalendarEvent[] => {
  const result: DensityCalendarEvent[] = [];

  findTimedClusters(toTimedSorted(events)).forEach(cluster => {
    if (cluster.length <= passThroughMax) {
      result.push(...cluster.map(item => item.event));
      return;
    }

    const buckets = new Map<number, TimedEvent[]>();
    cluster.forEach(item => {
      const key = bucketKey(item.event.start, bucketMinutes);
      buckets.set(key, [...(buckets.get(key) ?? []), item]);
    });

    buckets.forEach((bucketed, key) => {
      if (bucketed.length <= passThroughMax) {
        result.push(...bucketed.map(item => item.event));
        return;
      }

      const first = bucketed[0];
      // Clamp to 23:59 so a final-hour bucket (key = 23) doesn't roll the
      // cluster's end onto the next calendar day via setHours(24, …).
      const bucketEndMinutes = Math.min((key + 1) * bucketMinutes, 24 * 60 - 1);
      const bucketEnd = new Date(first.event.start);
      bucketEnd.setHours(Math.floor(bucketEndMinutes / 60), bucketEndMinutes % 60, 0, 0);

      result.push({
        id: `cluster-${first.event.id}-${key}`,
        title: `${bucketed.length} appointments`,
        start: new Date(Math.min(...bucketed.map(item => item.startMs))),
        end: bucketEnd,
        resourceId: first.event.resourceId,
        resource: first.event.resource,
        kind: 'cluster',
        members: [...bucketed]
          .sort((a, b) => a.startMs - b.startMs)
          .map(item => item.event),
      });
    });
  });

  return result.sort((a, b) => a.start.getTime() - b.start.getTime());
};

/**
 * Strict no-overlap lane layout: every real event gets an equal lane inside
 * its collision cluster, with a hairline gutter. Synthetic overflow pills do
 * not participate in lane packing — they're pinned to a fixed slim collector
 * column on the right edge (DevExtreme's compact Day/Week collector), so the
 * pills never steal width from the visible cards. Combined with
 * `capWithOverflow`, real-lane count is bounded so card width has a floor —
 * the audit's "guaranteed readable floor".
 */
const COLLECTOR_COLUMN_WIDTH = 12;

export const sideBySideLanesLayout: DayLayoutFunction<CalendarEvent> = ({
  events,
  slotMetrics,
  accessors,
}) => {
  const metrics = slotMetrics as SlotMetricsLike;
  const eventAccessors = accessors as EventAccessorsLike;
  const timed = toTimedSorted(events);
  const ranges = new Map(
    timed.map(item => [
      item.event,
      metrics.getRange(eventAccessors.start(item.event), eventAccessors.end(item.event)),
    ]),
  );

  const styled: StyledLayoutEvent[] = [];

  findTimedClusters(timed).forEach(cluster => {
    // `pills` is always empty in the production path (bucketClusterize emits
    // only 'cluster' events); the collector-column branch below runs only in
    // the Storybook "Variant 1" story. See capWithOverflow's header.
    const pills = cluster.filter(item => item.event.kind === 'overflow');
    const reals = cluster.filter(item => item.event.kind !== 'overflow');
    const { laneOf, laneCount } = assignLanes(reals);
    const usableWidth = pills.length > 0 ? 100 - COLLECTOR_COLUMN_WIDTH - 1 : 100;
    const laneWidth = laneCount > 0 ? usableWidth / laneCount : usableWidth;

    reals.forEach(item => {
      const range = ranges.get(item.event);
      if (!range) return;
      const lane = laneOf.get(item.event) ?? 0;
      styled.push({
        event: item.event,
        style: {
          top: range.top,
          height: range.height,
          width: laneWidth - 0.6,
          xOffset: lane * laneWidth + 0.3,
        } satisfies DensityEventStyle,
      });
    });

    pills.forEach(item => {
      const range = ranges.get(item.event);
      if (!range) return;
      styled.push({
        event: item.event,
        style: {
          top: range.top,
          height: range.height,
          width: COLLECTOR_COLUMN_WIDTH,
          xOffset: 100 - COLLECTOR_COLUMN_WIDTH,
        } satisfies DensityEventStyle,
      });
    });
  });

  return styled;
};

export interface CascadeOptions {
  /** Horizontal shift (% of column) added per stagger level. */
  indentPerLevel?: number;
  /** Cards never get narrower than this (%) from staggering alone. */
  minWidth?: number;
  /** Same-start groups split evenly while each share stays >= this (%). */
  minSplitWidth?: number;
  /** Inner stagger (%) once a same-start group is too big to split. */
  innerStagger?: number;
}

/**
 * Variant 2 layout — "bounded cascade".
 *
 * Events that start together split the available width like Google's
 * same-start columns. Events that start later overlay the running cards,
 * indented per stagger level with a clamped minimum width, so every card
 * keeps a readable left-edge strip (Bryntum's stagger geometry; FullCalendar
 * guarantees "at most half of each event will be obscured" the same way).
 * DOM paint order = start order, so later cards sit on top; hover raises and
 * expands the card (handled by the card component).
 */
export const createBoundedCascadeLayout = ({
  indentPerLevel = 11,
  minWidth = 56,
  minSplitWidth = 27,
  innerStagger = 7,
}: CascadeOptions = {}): DayLayoutFunction<CalendarEvent> => {
  return ({ events, minimumStartDifference, slotMetrics, accessors }) => {
    const metrics = slotMetrics as SlotMetricsLike;
    const eventAccessors = accessors as EventAccessorsLike;
    const timed = toTimedSorted(events);
    const ranges = new Map(
      timed.map(item => [
        item.event,
        metrics.getRange(eventAccessors.start(item.event), eventAccessors.end(item.event)),
      ]),
    );

    // Same-start groups: consecutive events whose range-start sits within
    // the minimum start difference of the group leader.
    interface StartGroup {
      items: TimedEvent[];
      rangeStart: number;
      maxEnd: number;
    }
    const groups: StartGroup[] = [];
    timed.forEach(item => {
      const range = ranges.get(item.event);
      if (!range) return;
      const lastGroup = groups[groups.length - 1];
      if (
        lastGroup &&
        Math.abs(range.start - lastGroup.rangeStart) < minimumStartDifference &&
        item.startMs < lastGroup.maxEnd
      ) {
        lastGroup.items.push(item);
        lastGroup.maxEnd = Math.max(lastGroup.maxEnd, item.endMs);
        return;
      }
      groups.push({ items: [item], rangeStart: range.start, maxEnd: item.endMs });
    });

    // Stagger level = how many earlier groups are still running when this
    // group starts.
    const active: { maxEnd: number }[] = [];
    const styled: StyledLayoutEvent[] = [];
    const maxIndent = 100 - minWidth;

    groups.forEach(group => {
      const groupStartMs = group.items[0].startMs;
      const depth = active.filter(entry => entry.maxEnd > groupStartMs).length;
      active.push({ maxEnd: group.maxEnd });

      const indent = Math.min(depth * indentPerLevel, maxIndent);
      const available = 100 - indent;
      const size = group.items.length;
      const evenShare = available / size;

      group.items.forEach((item, index) => {
        const range = ranges.get(item.event);
        if (!range) return;

        const splits = evenShare >= minSplitWidth;
        const width = splits
          ? evenShare
          : Math.max(available - (size - 1) * innerStagger, minWidth * 0.7);
        const xOffset = splits ? indent + index * evenShare : indent + index * innerStagger;

        styled.push({
          event: item.event,
          style: {
            top: range.top,
            height: range.height,
            width: Math.min(width, 100 - xOffset),
            xOffset,
          } satisfies DensityEventStyle,
        });
      });
    });

    return styled;
  };
};
